import $ from 'jquery'
import papaparse from 'papaparse'

import PoziAPIData from 'api/data'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { ShowWhatsHere, UseWhatsHereInfoPanel } from 'drawer/tabs/info/WhatsHereUtils'
import PoziMap from 'map/map'
import PoziNotifications from 'utils/notifications'

/**
 * PoziPropertyLookup module.
 *
 * A (Adapter)-> PoziPropertyLookup function is implemented in order to follow the standard Adapter pattern
 */
export default function PoziPropertyLookup () {
  // ============================================================
  // Public Variables
  // ============================================================

  const adapters = {
    wireup: function () {
      return wireup()
    },
    parseCSVFromModalInput: function (options) {
      return parseCSVFromModalInput(options)
    },
    test: function (args) {
      return test(args)
    }
  }

  const app = PoziUtils.getApp()

  // Due to 3rd party library (papaparse), had to create resolve/reject local vars for temporary promise event handlers.
  let _resolve
  let _reject

  // ============================================================
  // Private Variables
  // ============================================================

  // const CONSTANTS = {}

  // ============================================================
  // Public Functions
  // ============================================================

  /**
   * Wireup events
   */
  const wireup = function () {
    return new Promise(function (resolve, reject) {
      const bPropertyLookup = Model.get('config.layout.drawer.tabs.tools.propertylookup.visible', false)
      if (!bPropertyLookup) {
        $('#propertyLookupTool').hide()
        return resolve('PoziPropertyLookup: Hide button. Not licensed.')
      }

      const propertyLookupTool = document.getElementById('propertyLookupTool')
      if (!propertyLookupTool) {
        const err = 'PoziPropertyLookup: No menu button for property lookup.'
        return reject(err)
      } else {
        propertyLookupTool.addEventListener('click', clickedPropertyLookupMenu, false)
        const message = 'PoziPropertyLookup: Menu button event wiredup.'
        resolve(message)
      }
    })
  }

  /**
   * Example CSV:
        PROP_PFI,
        45415239,
        45415240,
   */
  const parseCSVFromModalInput = function (options = {}) {
    return new Promise(function (resolve, reject) {
      _resolve = resolve
      _reject = reject

      const files = $('#propertyLookupToolCsv')[0].files
      if (!files.length) {
        console.log('PoziPropertyLookup: No file choosen?? Should be impossible due to button disable/enable.')
        enableLookupButton()
        const err = 'No file choosen'
        return reject(err)
      }

      const csvFile = files[0] // take first uploaded file

      const parseCfg = {
        header: true,
        complete: parseSuccess,
        error: parseFailed,

        ...options
      }

      // Do the actual parsing
      papaparse.parse(csvFile, parseCfg)
    })
  }

  /**
   * Wireup events
   */
  const test = function (args) {
    const err = 'PoziPropertyLookup: Test NYI'
    throw err
  }

  // ============================================================
  // Private Functions
  // ============================================================

  const clickedPropertyLookupMenu = function (e) {
    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: 'Property lookup button',
      action: 'Click property lookup button'
    })
    console.log('PoziPropertyLookup: Property lookup menu clicked...')
    showCSVSelector()

    const propertyLookupToolGo = document.getElementById('propertyLookupToolGo')
    propertyLookupToolGo.addEventListener('click', clickSearchProperties, false)

    const csvfileInput = document.getElementById('propertyLookupToolCsv')
    csvfileInput.addEventListener('change', enableLookupButton, false)

    disableLookupButton()
  }

  const clickSearchProperties = function () {
    console.log('PoziPropertyLookup: Click Search Properties...')
    disableLookupButton()

    PoziMap().removeMarker()

    // This is a bit of black magic based on a Bootstrap trigger.
    // Essentially, this will eventually trigger a call to property-info.js which detects if this model is open.
    // The property info code then calls the adapter interface parseCSVFromModalInput
    Model.set('app.propertylookup.fromcsv', true)
    if (UseWhatsHereInfoPanel()) {
      parseCSV()
    } else {
      app.activateMode('default') // Triggers card expansion.
    }
  }

  const parseCSV = function () {
    const maxFeatures = Model.get('config.settings.maxfeatures', 100) // This is set in templates.json

    const parseOptions = {
      delimiter: ','
    }

    parseCSVFromModalInput(parseOptions)
      .then(function (results) {
        // console.log(results)

        // Convert all keys of CSV entries to lowercase, i.e. PROP_PFI becomes prop_pfi
        // Inspired by: https://stackoverflow.com/a/12540603
        results = results.map(result => Object.fromEntries(
          Object.entries(result).map(([key, value]) => [key.toLowerCase(), value])
        ))

        console.log('Parse CSV results', results)

        // Build a csv array of Property PFI numbers.
        const pfiArray = []
        for (const key in results) {
          if (!Object.hasOwnProperty.call(results, key)) continue
          if (results[key].prop_pfi && results[key].prop_pfi !== '') {
            pfiArray.push(results[key].prop_pfi)
          }
          if (pfiArray.length > maxFeatures) {
            PoziNotifications().warning('Not all properties shown. The maximum number of properties at one time is ' + maxFeatures + '.')
            break
          }
        }

        const projection = '4326'

        const cqlfilter = 'prop_pfi IN (' + pfiArray + ')'
        console.log('PoziGetPropertyLookup: ', cqlfilter)

        const url =
        'https://opendata.maps.vic.gov.au/geoserver/ows?SERVICE=WFS&VERSION=1.1.0&REQUEST=GetFeature&srsName=EPSG%3A' +
        projection +
        '&typeNames=v_property_mp_address&maxFeatures=' +
        maxFeatures +
        '&outputFormat=application%2Fjson&CQL_FILTER=' +
        cqlfilter +
        '&_t=' +
        Date.now()

        return PoziAPIData()
          .ajax({
            method: 'GET',
            url,
            error: dataRetrievalFailed,
            success: dataRetrievalSuccess
          })
          .catch(error => {
            console.warn('Error fetching URL', error)
            throw (new Error(`could not fetch URL: ${url}`))
          })
      })
      .catch(function (error) {
        console.log('PoziGetPropertyLookup error:', error)
        PoziNotifications().warning(String(error))
      })
  }

  const dataRetrievalFailed = function (jqXHR, status, errorThrown) {
    console.log('DataRetrievalFailed set message')
  }

  const dataRetrievalSuccess = function (featureCollection) {
    console.log('DataRetrievalSuccess set message')
    ShowWhatsHere(featureCollection)
  }

  const parseSuccess = function (results) {
    console.debug('parseSuccess', results)

    enableLookupButton()

    if (results && results.errors) {
      if (results.errors && results.errors.length > 0) {
        console.log('PoziPropertyLookup: Errors: ', results.errors)
      }
      if (results.data && results.data.length > 0) {
        // console.log("PoziPropertyLookup: Data", results.data)
        console.log('PoziPropertyLookup: Data retrieved.')
        _resolve(results.data)
      }
      _reject('Unable to get results')
    }

    // console.log("PoziPropertyLookup: Parse complete");
    // console.log("PoziPropertyLookup: Results:", results);
  }

  const parseFailed = function (err, file) {
    console.log('PoziPropertyLookup: ERROR:', err, file)

    disableLookupButton()
    _reject(err)
  }

  const showCSVSelector = function () {
    $('#propertyLookupToolInfo').show()
  }

  const disableLookupButton = function () {
    $('#propertyLookupToolGo').prop('disabled', true)
    $('#propertyLookupToolGo').css({
      'background-color': '#0000001f',
      color: '#00000042',
      cursor: 'not-allowed'
    })
  }

  const enableLookupButton = function () {
    $('#propertyLookupToolGo').prop('disabled', false)
    $('#propertyLookupToolGo').css({
      'background-color': '#00000000',
      color: '#737373',
      cursor: 'pointer'
    })
  }

  // Return adapters (must be at end of adapter)
  return adapters
}
